// ============================================
// EXPEDIENTEDETALLE.JS — Ficha completa de un expediente
// ============================================

import React, { useEffect, useState } from 'react';
import {
  API_BASE_EXP, PP_EXP, COL_EXP, ESTADOS_EXP, PRIORIDADES, TIPOS_EVENTO,
  estiloInputExp, estiloBotonExp, estiloBotonSecExp, estiloTarjetaExp,
  formatMoneda, formatFechaHora, SEVERIDADES,
} from './helpersExpedientes';

const ESTADOS_ORDEN = ['creado','por_cotizar','cotizado','por_programar','programado','en_ejecucion','por_facturar','facturado','pagado'];

export default function ExpedienteDetalle({ id, onVolver }) {
  const [data, setData] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [modalEvento, setModalEvento] = useState(null);
  const [modalEstado, setModalEstado] = useState(false);
  const [modalRecurrencia, setModalRecurrencia] = useState(false);

  const cargar = async () => {
    setCargando(true);
    try {
      const r = await fetch(`${API_BASE_EXP}/api/expedientes/${id}`).then(x => x.json());
      if (r.ok) setData(r);
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => { cargar(); }, [id]);

  const resolverAlerta = async (alertaId) => {
    await fetch(`${API_BASE_EXP}/api/expedientes/alertas/${alertaId}/resolver`, { method: 'POST' });
    cargar();
  };

  if (cargando || !data) {
    return <div style={{ padding: 40, textAlign: 'center', color: COL_EXP.grisClaro, fontFamily: PP_EXP }}>Cargando…</div>;
  }

  const { expediente: e, eventos, historial, alertas } = data;
  const estado = ESTADOS_EXP[e.estado] || { label: e.estado, color: COL_EXP.grisClaro, bg: COL_EXP.fondo };
  const prioridad = PRIORIDADES[e.prioridad] || PRIORIDADES.normal;

  return (
    <div style={{ fontFamily: PP_EXP, background: COL_EXP.fondo, minHeight: '100%', color: COL_EXP.gris }}>
      {/* Cabecera */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12, padding: '16px 24px',
        background: '#fff', borderBottom: `1px solid ${COL_EXP.borde}`,
      }}>
        <button onClick={onVolver} style={{ background: 'transparent', border: 'none', fontSize: 18, cursor: 'pointer', color: COL_EXP.oscuro }}>←</button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: COL_EXP.oscuro }}>
              {e.tipo_icono} {e.titulo}
            </div>
            <span style={{ ...chip, background: estado.bg, color: estado.color }}>{estado.label}</span>
            <span style={{ ...chip, background: prioridad.color + '20', color: prioridad.color }}>{prioridad.label}</span>
          </div>
          <div style={{ fontSize: 12, color: COL_EXP.grisClaro, marginTop: 2 }}>
            {e.folio} · {e.tipo_nombre}
            {e.grupo_nombre && ` · ${e.grupo_nombre}`}
            {e.razon_social_nombre && ` · ${e.razon_social_nombre}`}
            {e.sucursal_nombre && ` · ${e.sucursal_nombre}`}
          </div>
        </div>
        <button style={estiloBotonSecExp} onClick={() => setModalEstado(true)}>Cambiar estado</button>
      </div>

      <div style={{ padding: 24, display: 'grid', gap: 14, maxWidth: 960, margin: '0 auto' }}>
        {/* Alertas */}
        {alertas.length > 0 && (
          <div style={{ ...estiloTarjetaExp, borderColor: '#f0d9b8', background: '#fff4e5' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#a86b1e', marginBottom: 10 }}>
              ⚠️ Alertas activas ({alertas.length})
            </div>
            <div style={{ display: 'grid', gap: 6 }}>
              {alertas.map(a => {
                const sev = SEVERIDADES[a.severidad] || SEVERIDADES.media;
                return (
                  <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', background: '#fff', borderRadius: 6 }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: sev.color }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 600 }}>{a.tipo}</div>
                      <div style={{ fontSize: 11, color: COL_EXP.grisClaro }}>{a.mensaje}</div>
                    </div>
                    <button style={estiloBotonSecExp} onClick={() => resolverAlerta(a.id)}>Resolver</button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Datos generales */}
        <div style={estiloTarjetaExp}>
          <div style={{ fontSize: 13, fontWeight: 700, color: COL_EXP.oscuro, marginBottom: 10 }}>Datos</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14 }}>
            <Campo lbl="Monto estimado" val={formatMoneda(e.monto_estimado)} />
            <Campo lbl="Cotizado" val={formatMoneda(e.monto_cotizado)} />
            <Campo lbl="Facturado" val={formatMoneda(e.monto_facturado)} />
            <Campo lbl="Pagado" val={formatMoneda(e.monto_pagado)} />
            <Campo lbl="Días en estado actual" val={`${e.dias_en_estado || 0} día(s)`} />
            <Campo lbl="Creado" val={formatFechaHora(e.fecha_creacion)} />
          </div>
          {e.descripcion && (
            <div style={{ marginTop: 10, fontSize: 12, color: COL_EXP.gris, whiteSpace: 'pre-wrap' }}>
              {e.descripcion}
            </div>
          )}
        </div>

        {/* Eventos */}
        <div style={estiloTarjetaExp}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: COL_EXP.oscuro }}>
              📅 Eventos ({eventos.length})
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              {e.contrato_recurrencia && (
                <button style={estiloBotonSecExp} onClick={() => setModalRecurrencia(true)}>
                  + Próxima recurrencia
                </button>
              )}
              <button style={estiloBotonExp} onClick={() => setModalEvento({ nuevo: true })}>+ Evento</button>
            </div>
          </div>
          {eventos.length === 0 && <div style={{ fontSize: 12, color: COL_EXP.grisClaro }}>Sin eventos programados.</div>}
          <div style={{ display: 'grid', gap: 6 }}>
            {eventos.map(ev => (
              <EventoRow key={ev.id} evento={ev} onClick={() => setModalEvento({ evento: ev })} onActualizar={cargar} />
            ))}
          </div>
        </div>

        {/* Historial */}
        <div style={estiloTarjetaExp}>
          <div style={{ fontSize: 13, fontWeight: 700, color: COL_EXP.oscuro, marginBottom: 10 }}>
            📜 Historial
          </div>
          <div style={{ display: 'grid', gap: 6 }}>
            {historial.map(h => (
              <div key={h.id} style={{ fontSize: 12, padding: '6px 0', borderBottom: `1px dashed ${COL_EXP.borde}` }}>
                <span style={{ color: COL_EXP.grisClaro }}>{formatFechaHora(h.creado_en)}</span>
                {' — '}
                <strong>{h.estado_anterior || '—'} → {h.estado_nuevo}</strong>
                {h.motivo && <span style={{ color: COL_EXP.grisClaro }}> · {h.motivo}</span>}
              </div>
            ))}
          </div>
        </div>
      </div>

      {modalEvento && (
        <EventoModal
          expedienteId={id}
          evento={modalEvento.evento || null}
          onCerrar={() => setModalEvento(null)}
          onGuardado={() => { setModalEvento(null); cargar(); }}
        />
      )}
      {modalEstado && (
        <CambiarEstadoModal
          expedienteId={id}
          estadoActual={e.estado}
          onCerrar={() => setModalEstado(false)}
          onCambiado={() => { setModalEstado(false); cargar(); }}
        />
      )}
      {modalRecurrencia && (
        <RecurrenciaModal
          expedienteId={id}
          onCerrar={() => setModalRecurrencia(false)}
          onCreado={() => { setModalRecurrencia(false); cargar(); }}
        />
      )}
    </div>
  );
}

function Campo({ lbl, val }) {
  return (
    <div>
      <div style={{ fontSize: 10, fontWeight: 600, color: COL_EXP.grisClaro, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{lbl}</div>
      <div style={{ fontSize: 13, color: COL_EXP.gris, marginTop: 3 }}>{val}</div>
    </div>
  );
}

function EventoRow({ evento, onClick, onActualizar }) {
  const estado = evento.estado;
  const bg = estado === 'completado' ? '#e8faf5' : estado === 'en_curso' ? '#fff8ed' : COL_EXP.fondo;
  const color = estado === 'completado' ? '#1D9E75' : estado === 'en_curso' ? '#f59e0b' : COL_EXP.principal;

  const iniciar = async (e) => {
    e.stopPropagation();
    const payload = {};
    if (navigator.geolocation) {
      try {
        const pos = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 });
        });
        payload.gps_lat = pos.coords.latitude;
        payload.gps_lng = pos.coords.longitude;
      } catch { /* sin gps */ }
    }
    await fetch(`${API_BASE_EXP}/api/expedientes/eventos/${evento.id}/iniciar`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    onActualizar();
  };

  const completar = async (e) => {
    e.stopPropagation();
    const nombre = window.prompt('Nombre de quien recibe el trabajo:');
    if (!nombre) return;
    const notas = window.prompt('Notas de cierre (opcional):') || '';
    await fetch(`${API_BASE_EXP}/api/expedientes/eventos/${evento.id}/completar`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ nombre_firmante: nombre, notas_cierre: notas }),
    });
    onActualizar();
  };

  return (
    <div onClick={onClick} style={{
      display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
      background: bg, borderRadius: 8, cursor: 'pointer',
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: COL_EXP.oscuro }}>
          {evento.tipo_evento} · {formatFechaHora(evento.fecha_inicio)}
        </div>
        {evento.titulo && <div style={{ fontSize: 11, color: COL_EXP.grisClaro }}>{evento.titulo}</div>}
      </div>
      <span style={{ ...chip, background: color + '20', color }}>{estado}</span>
      {estado === 'programado' && (
        <button onClick={iniciar} style={{ ...estiloBotonSecExp, padding: '4px 8px', fontSize: 10 }}>▶ Iniciar</button>
      )}
      {estado === 'en_curso' && (
        <button onClick={completar} style={{ ...estiloBotonExp, padding: '4px 8px', fontSize: 10 }}>✓ Completar</button>
      )}
    </div>
  );
}

function EventoModal({ expedienteId, evento, onCerrar, onGuardado }) {
  const [form, setForm] = useState(evento || { tipo_evento: 'visita', titulo: '', fecha_inicio: '', duracion_minutos: 60, tecnico_id: '', descripcion: '' });
  const [tecnicos, setTecnicos] = useState([]);
  const [conflicto, setConflicto] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('beep_token');
    const h = token ? { Authorization: `Bearer ${token}` } : {};
    fetch(`${API_BASE_EXP}/api/usuarios`, { headers: h }).then(r => r.json()).catch(() => ({ usuarios: [] }))
      .then(u => setTecnicos(u.usuarios || []));
  }, []);

  const guardar = async (force = false) => {
    setGuardando(true); setError(null); setConflicto(null);
    try {
      const body = {
        tipo_evento: form.tipo_evento,
        titulo: form.titulo || null,
        descripcion: form.descripcion || null,
        fecha_inicio: form.fecha_inicio,
        duracion_minutos: Number(form.duracion_minutos) || 60,
        tecnico_id: form.tecnico_id || null,
        force,
      };
      const url = evento && evento.id
        ? `${API_BASE_EXP}/api/expedientes/eventos/${evento.id}`
        : `${API_BASE_EXP}/api/expedientes/${expedienteId}/eventos`;
      const method = evento && evento.id ? 'PATCH' : 'POST';
      const resp = await fetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      const r = await resp.json();
      if (resp.status === 409 && r.codigo === 'conflicto_tecnico') {
        setConflicto(r.eventos_solapados || []);
        return;
      }
      if (!r.ok) throw new Error(r.error || 'Error');
      onGuardado();
    } catch (e) {
      setError(e.message);
    } finally {
      setGuardando(false);
    }
  };

  const eliminar = async () => {
    if (!evento || !evento.id) return;
    if (!window.confirm('¿Eliminar evento?')) return;
    await fetch(`${API_BASE_EXP}/api/expedientes/eventos/${evento.id}`, { method: 'DELETE' });
    onGuardado();
  };

  return (
    <div style={overlay} onClick={onCerrar}>
      <div style={card} onClick={(e) => e.stopPropagation()}>
        <div style={{ fontSize: 16, fontWeight: 700, color: COL_EXP.oscuro, marginBottom: 14 }}>
          {evento && evento.id ? 'Editar evento' : 'Nuevo evento'}
        </div>

        <div style={{ display: 'grid', gap: 10 }}>
          <div>
            <label style={lbl}>Tipo</label>
            <select style={estiloInputExp} value={form.tipo_evento} onChange={(e) => setForm(f => ({ ...f, tipo_evento: e.target.value }))}>
              {TIPOS_EVENTO.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <label style={lbl}>Título (opcional)</label>
            <input style={estiloInputExp} value={form.titulo || ''} onChange={(e) => setForm(f => ({ ...f, titulo: e.target.value }))} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 100px', gap: 8 }}>
            <div>
              <label style={lbl}>Fecha y hora</label>
              <input type="datetime-local" style={estiloInputExp}
                value={form.fecha_inicio ? String(form.fecha_inicio).slice(0, 16) : ''}
                onChange={(e) => setForm(f => ({ ...f, fecha_inicio: e.target.value }))} />
            </div>
            <div>
              <label style={lbl}>Duración (min)</label>
              <input type="number" style={estiloInputExp} value={form.duracion_minutos || 60} onChange={(e) => setForm(f => ({ ...f, duracion_minutos: e.target.value }))} />
            </div>
          </div>
          <div>
            <label style={lbl}>Técnico</label>
            <select style={estiloInputExp} value={form.tecnico_id || ''} onChange={(e) => setForm(f => ({ ...f, tecnico_id: e.target.value }))}>
              <option value="">— Sin asignar —</option>
              {tecnicos.map(u => <option key={u.id} value={u.id}>{u.nombre}</option>)}
            </select>
          </div>
          <div>
            <label style={lbl}>Descripción</label>
            <textarea style={{ ...estiloInputExp, minHeight: 50, resize: 'vertical' }} value={form.descripcion || ''} onChange={(e) => setForm(f => ({ ...f, descripcion: e.target.value }))} />
          </div>
        </div>

        {conflicto && (
          <div style={{ marginTop: 12, padding: 10, background: '#fff4e5', border: '1px solid #f0d9b8', borderRadius: 8, fontSize: 12 }}>
            <div style={{ fontWeight: 700, color: '#a86b1e', marginBottom: 6 }}>⚠️ Conflicto detectado</div>
            <div style={{ color: COL_EXP.gris, marginBottom: 8 }}>
              El técnico ya tiene {conflicto.length} evento(s) solapados:
            </div>
            <div style={{ display: 'grid', gap: 4 }}>
              {conflicto.map(c => (
                <div key={c.id} style={{ fontSize: 11, color: COL_EXP.grisClaro }}>
                  · {formatFechaHora(c.fecha_inicio)} — {c.titulo || c.tipo_evento} ({c.folio})
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
              <button style={estiloBotonSecExp} onClick={() => setConflicto(null)}>Cambiar hora/técnico</button>
              <button style={estiloBotonExp} onClick={() => guardar(true)}>Confirmar de todos modos</button>
            </div>
          </div>
        )}

        {error && <div style={{ color: '#e05c5c', fontSize: 12, marginTop: 10 }}>{error}</div>}

        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginTop: 20 }}>
          {evento && evento.id ? (
            <button style={{ ...estiloBotonSecExp, color: '#e05c5c' }} onClick={eliminar}>🗑 Eliminar</button>
          ) : <span />}
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={estiloBotonSecExp} onClick={onCerrar}>Cancelar</button>
            <button style={estiloBotonExp} onClick={() => guardar(false)} disabled={guardando || !form.fecha_inicio}>
              {guardando ? 'Guardando…' : 'Guardar'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function CambiarEstadoModal({ expedienteId, estadoActual, onCerrar, onCambiado }) {
  const [nuevo, setNuevo] = useState(estadoActual);
  const [motivo, setMotivo] = useState('');
  const [error, setError] = useState(null);
  const [guardando, setGuardando] = useState(false);

  const idxActual = ESTADOS_ORDEN.indexOf(estadoActual);
  const idxNuevo = ESTADOS_ORDEN.indexOf(nuevo);
  const salto = idxActual >= 0 && idxNuevo >= 0 && idxNuevo > idxActual + 1;

  const guardar = async () => {
    setGuardando(true); setError(null);
    try {
      const resp = await fetch(`${API_BASE_EXP}/api/expedientes/${expedienteId}/cambiar-estado`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ estado_nuevo: nuevo, motivo }),
      });
      const r = await resp.json();
      if (!r.ok) throw new Error(r.error || 'Error');
      onCambiado();
    } catch (e) {
      setError(e.message);
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div style={overlay} onClick={onCerrar}>
      <div style={card} onClick={(e) => e.stopPropagation()}>
        <div style={{ fontSize: 16, fontWeight: 700, color: COL_EXP.oscuro, marginBottom: 14 }}>
          Cambiar estado
        </div>
        <div style={{ fontSize: 12, color: COL_EXP.grisClaro, marginBottom: 10 }}>
          Actual: <strong>{ESTADOS_EXP[estadoActual]?.label || estadoActual}</strong>
        </div>
        <label style={lbl}>Nuevo estado</label>
        <select style={estiloInputExp} value={nuevo} onChange={(e) => setNuevo(e.target.value)}>
          {Object.entries(ESTADOS_EXP).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        {salto && (
          <div style={{ marginTop: 10, padding: 10, background: '#fff4e5', borderRadius: 6, fontSize: 11, color: '#a86b1e' }}>
            ⚠️ Saltas etapas en la secuencia. El motivo es obligatorio.
          </div>
        )}
        <div style={{ marginTop: 10 }}>
          <label style={lbl}>Motivo {salto && '*'}</label>
          <textarea style={{ ...estiloInputExp, minHeight: 60 }} value={motivo} onChange={(e) => setMotivo(e.target.value)} />
        </div>
        {error && <div style={{ color: '#e05c5c', fontSize: 12, marginTop: 10 }}>{error}</div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
          <button style={estiloBotonSecExp} onClick={onCerrar}>Cancelar</button>
          <button style={estiloBotonExp} onClick={guardar} disabled={guardando}>
            {guardando ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
}

function RecurrenciaModal({ expedienteId, onCerrar, onCreado }) {
  const [fecha, setFecha] = useState('');
  const [tecnicoId, setTecnicoId] = useState('');
  const [tecnicos, setTecnicos] = useState([]);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('beep_token');
    const h = token ? { Authorization: `Bearer ${token}` } : {};
    fetch(`${API_BASE_EXP}/api/usuarios`, { headers: h }).then(r => r.json()).catch(() => ({ usuarios: [] }))
      .then(u => setTecnicos(u.usuarios || []));
  }, []);

  const guardar = async () => {
    if (!fecha) return;
    setGuardando(true);
    try {
      await fetch(`${API_BASE_EXP}/api/expedientes/${expedienteId}/proxima-recurrencia`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fecha_inicio: fecha, tecnico_id: tecnicoId || null }),
      });
      onCreado();
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div style={overlay} onClick={onCerrar}>
      <div style={card} onClick={(e) => e.stopPropagation()}>
        <div style={{ fontSize: 16, fontWeight: 700, color: COL_EXP.oscuro, marginBottom: 14 }}>
          📅 Programar próxima visita (recurrencia)
        </div>
        <div style={{ display: 'grid', gap: 10 }}>
          <div>
            <label style={lbl}>Fecha y hora</label>
            <input type="datetime-local" style={estiloInputExp} value={fecha} onChange={(e) => setFecha(e.target.value)} />
          </div>
          <div>
            <label style={lbl}>Técnico</label>
            <select style={estiloInputExp} value={tecnicoId} onChange={(e) => setTecnicoId(e.target.value)}>
              <option value="">— Sin asignar —</option>
              {tecnicos.map(u => <option key={u.id} value={u.id}>{u.nombre}</option>)}
            </select>
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
          <button style={estiloBotonSecExp} onClick={onCerrar}>Cancelar</button>
          <button style={estiloBotonExp} onClick={guardar} disabled={!fecha || guardando}>
            {guardando ? 'Creando…' : 'Crear visita'}
          </button>
        </div>
      </div>
    </div>
  );
}

const overlay = {
  position: 'fixed', inset: 0, background: 'rgba(26,58,92,0.5)', zIndex: 1100,
  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
};
const card = {
  background: '#fff', borderRadius: 14, padding: 24, width: '100%', maxWidth: 520,
  fontFamily: PP_EXP, color: COL_EXP.gris, maxHeight: 'calc(var(--beep-vh) * 92)', overflowY: 'auto',
};
const lbl = { fontSize: 11, fontWeight: 600, color: COL_EXP.grisClaro };
const chip = { fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 4, textTransform: 'uppercase', letterSpacing: '0.04em' };
